/**
 * Category Cockpit — Journal: every write to the knowledge graph of the sector, newest first,
 * as the tables record it today (gap decisions, suggestions, the events table, schema
 * creations, research runs, hints, readiness moves, page reviews). Derived, never stored.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { fetchJournal, type Journal, type JournalKind } from '@/services/categoryCockpitService';
import { Shell, useCockpit } from './components/Shell';
import { ErrorBox, Loading } from './components/bits';

function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

export default function CategoryCockpitJournal() {
  return (
    <Shell active="journal">
      <JournalBody />
    </Shell>
  );
}

/** how many journal rows the sector table draws before it offers the rest */
const JOURNAL_PAGE = 300;

function JournalBody() {
  const { sectorId } = useCockpit();
  const [data, setData] = useState<Journal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState('all');
  const [kind, setKind] = useState<JournalKind | 'all'>('all');
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (sectorId == null) return undefined;
    const ac = new AbortController();
    setLoading(true); setError(null); setData(null);
    fetchJournal(sectorId, undefined, ac.signal)
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { if (isAbort(err)) return; setError(err?.message || String(err)); setLoading(false); });
    return () => ac.abort();
  }, [sectorId]);

  const scopes = useMemo(() => [...new Set((data?.entries ?? []).map((e) => e.scope))].sort(), [data]);
  const kinds = useMemo(() => [...new Set((data?.entries ?? []).map((e) => e.kind))], [data]);
  const rows = useMemo(() => (data?.entries ?? []).filter((e) => (scope === 'all' || e.scope === scope) && (kind === 'all' || e.kind === kind) && (!q || `${e.what} ${e.detail || ''}`.toLowerCase().includes(q.toLowerCase()))), [data, scope, kind, q]);
  /** the table shows a page at a time; the count above it is the filtered total, not the page */
  const page = all ? rows : rows.slice(0, JOURNAL_PAGE);


  if (loading) return <Loading label="Deriving the journal from the seven tables that record writes…" />;
  if (error) return <ErrorBox title="The journal could not be loaded" msg={error} />;
  if (!data) return null;

  return (
    <>
      <h1 className="pmk-h1">Journal</h1>
      <p className="pmk-sub">Every write to the knowledge graph of {data.sector.name}, newest first, as the tables record it today: research runs, gap decisions, schema changes, hints, readiness moves, page reviews. No table shares a history, so each entry is derived from the timestamps and statuses that exist — this is what a real version history would start from.</p>
      {!!(data.truncated || []).length && (
        <div className="pmk-note warn">
          <b>This is bounded history, not all of it.</b>{' '}
          {(data.truncated || []).map((t) => `${t.source.replace(/_/g, ' ')} stops at ${t.limit} groups`).join(' · ')}.
          Older writes of those kinds are not on this screen. A single category’s journal is read separately and is not cut this way.
        </div>
      )}
      <div className="pmc-panel">
        <div className="pmk-filters">
          <label>Scope
            <select className="pmc-select" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="all">all</option>
              {scopes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>Kind
            <select className="pmc-select" value={kind} onChange={(e) => setKind(e.target.value as JournalKind | 'all')}>
              <option value="all">all</option>
              {kinds.map((k) => <option key={k} value={k}>{data.kinds[k] || k}</option>)}
            </select>
          </label>
          <input className="pmc-input" type="search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the journal" />
          <span className="pmc-grow" />
          <span className="pmc-small pmc-mute">{rows.length} entries · {data._meta.source}{data.cached ? ' · cached' : ''}</span>
        </div>
        <div className="pmk-tablewrap">
          <table className="pmk-jr">
            <thead><tr><th>When</th><th>Scope</th><th>Kind</th><th>What happened</th><th>Who · how</th><th>Reversible</th><th className="tbl">Table</th></tr></thead>
            <tbody>
              {page.map((e, i) => (
                <tr key={`${e.at}-${e.kind}-${e.scope}-${i}`}>
                  <td className="when">{String(e.at).slice(0, 10)}</td>
                  <td>{e.scope}</td>
                  <td><span className={`pmk-kind ${e.kind}`}>{data.kinds[e.kind] || e.kind}</span></td>
                  <td className="what">{e.what}{e.detail && <span className="dt" title={e.detail}>{e.detail}</span>}</td>
                  <td className="pmc-small">{e.who}{e.how ? <span className="pmc-mute"> · {e.how}</span> : null}</td>
                  <td className="pmc-small" data-tip={e.rev[1]}>{e.rev[0]}</td>
                  <td className="tbl">{e.tbl}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7}><div className="pmc-empty"><b>No entry.</b> No write matches these filters.</div></td></tr>}
            </tbody>
          </table>
          {rows.length > JOURNAL_PAGE && (
            <div className="pmk-jr-more">
              <span className="pmc-small pmc-mute">Showing {page.length} of {rows.length} entries.</span>
              <button type="button" className="pmc-btn" onClick={() => setAll(!all)}>
                {all ? `Show the first ${JOURNAL_PAGE}` : `Show all ${rows.length}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
